import { useContext, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import clsx from 'clsx';
import * as R from 'ramda';

import CreateEditSchemaModal from '@atom/components/common/createEditSchemaModal/CreateEditSchemaModal';
import DeleteModal from '@atom/components/common/DeleteModal';
import editSchemaIcon from '@atom/components/common/svgIcons/editSchemaIcon.svg';
import lineTypeIconWhite from '@atom/components/common/svgIcons/lineTypeIconWhite.svg';
import tabIcon from '@atom/components/common/svgIcons/tabIcon.svg';
import SchemaDetailContext, {
  PreviewTab,
  SchemaDetailView,
} from '@atom/components/schemaDetail/SchemaDetailContext';
import { getConversionUnitBulkInputs } from '@atom/components/schemaDetail/schemaPreview/costDetail/costDetailUtilities';
import textConstants from '@atom/constants/textConstants';
import {
  SCHEMA_CONVERSION_UNIT_CREATE_BULK,
  SCHEMA_CONVERSION_UNIT_DELETE_BULK,
  SCHEMA_CONVERSION_UNIT_UPDATE_BULK,
  SCHEMA_DELETE,
  SCHEMA_UPDATE,
} from '@atom/graph/schema';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Icon, IconButton, Progress, Snackbar } from '@atom/mui';
import {
  ArrowBackIcon,
  CategoryIcon,
  DeleteIcon,
  EditIcon,
  InfoIcon,
  ListIcon,
  LocationOnIcon,
  MonetizationOnIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import {
  ConversionUnit,
  LocationType,
  SchemaSavePayload,
} from '@atom/types/schema';
import {
  getErrorCode,
  isNilOrEmpty,
} from '@atom/utilities/validationUtilities';

import HeaderNavigationButton from './HeaderNavigationButton';
import SchemaStatus from './SchemaStatus';

import classes from './schemaDetailHeader.module.css';

const getTabs = (isMaterial: boolean) => {
  return [
    {
      value: PreviewTab.INFO,
      label: 'info',
      icon: InfoIcon,
    },
    {
      value: PreviewTab.SUB_ITEMS,
      label: 'sub items',
      icon: ListIcon,
    },
    ...(isMaterial
      ? [
          {
            value: PreviewTab.COST,
            label: 'cost',
            icon: MonetizationOnIcon,
          },
        ]
      : []),
  ];
};

const getLocationTypeIcon = (locationType: LocationType) => {
  switch (locationType) {
    case LocationType.POINT:
      return <Icon icon={LocationOnIcon} color={colors.neutral.white} />;
    case LocationType.LINE_STRING:
      return <img src={lineTypeIconWhite} />;
    default:
      return <Icon icon={CategoryIcon} color={colors.neutral.white} />;
  }
};

const SchemaDetailHeader = () => {
  const {
    view,
    setView,
    schemaTree,
    refetchSchemaTree,
    loading,
    pendingUpdates,
    setPendingUpdates,
    pendingGroupUpdates,
    setPendingGroupUpdates,
    pendingCreations,
    setPendingCreations,
    pendingSchemaUpdates,
    pendingConversionUnits,
    setPendingConversionUnits,
  } = useContext(SchemaDetailContext);
  const [, { backDestination, navigate }] = useNavigation();

  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDiscard, setOpenDiscard] = useState<boolean>(false);

  const [deleteSchema] = useMutation(SCHEMA_DELETE);

  const [updateSchema] = useMutation(SCHEMA_UPDATE);

  const [createConversionUnits] = useMutation(
    SCHEMA_CONVERSION_UNIT_CREATE_BULK,
  );

  const [updateConversionUnits] = useMutation(
    SCHEMA_CONVERSION_UNIT_UPDATE_BULK,
  );

  const [deleteConversionUnits] = useMutation(
    SCHEMA_CONVERSION_UNIT_DELETE_BULK,
  );

  const isPreviewView = view === SchemaDetailView.PREVIEW;
  const hasPendingChanges =
    !isNilOrEmpty(pendingUpdates) ||
    !isNilOrEmpty(pendingCreations) ||
    !isNilOrEmpty(pendingGroupUpdates) ||
    !isNilOrEmpty(pendingSchemaUpdates) ||
    !R.isNil(pendingConversionUnits);

  const navigateBack = () => {
    setPendingUpdates(null);
    setPendingCreations(null);
    setPendingGroupUpdates(null);
    setPendingConversionUnits(null);
    navigate(backDestination('/admin/inventoryTypes'));
  };

  const handleBackClick = () => {
    if (hasPendingChanges) {
      setOpenDiscard(true);
      return;
    }

    navigateBack();
  };

  const toggleView = () => {
    setView(
      isPreviewView ? SchemaDetailView.BUILDER : SchemaDetailView.PREVIEW,
    );
  };

  const handleUpdate = async (
    payload: SchemaSavePayload,
    conversionUnits?: ConversionUnit[],
  ) => {
    const schemaId = schemaTree?.id;
    const savedConversionUnits = schemaTree?.budget?.conversionUnits ?? [];
    const { creates, updates, deletes } = getConversionUnitBulkInputs(
      conversionUnits ?? savedConversionUnits,
      savedConversionUnits,
    );

    try {
      // removed units belong to the old cost type and unit, so they go before
      // the schema update, matching CostDetail
      if (!R.isEmpty(deletes)) {
        await deleteConversionUnits({
          variables: { input: { schemaId, conversionUnitIds: deletes } },
        });
      }

      await updateSchema({
        variables: {
          input: {
            schemaId,
            assetType: payload?.name,
            ...payload,
            locationType: payload?.locationType ?? '',
          },
        },
      });

      // creates go before updates so an isDefault: true sent in the update
      // wins, since the server clears the previous default
      if (!R.isEmpty(creates)) {
        await createConversionUnits({
          variables: { input: { schemaId, conversionUnits: creates } },
        });
      }

      if (!R.isEmpty(updates)) {
        await updateConversionUnits({
          variables: { input: { schemaId, conversionUnits: updates } },
        });
      }
    } catch (error) {
      Snackbar.error({ message: 'Failed to update inventory type.' });
    }

    refetchSchemaTree();
    setOpenEdit(false);
  };

  const handleDelete = async () => {
    try {
      Snackbar.info({
        message: textConstants.GENERIC_APPLICATION_DELETING_TEXT,
      });

      await deleteSchema({
        variables: {
          id: schemaTree?.id,
        },
      });

      Snackbar.info({
        message: 'Successfully deleted inventory type.',
      });

      setOpenDelete(false);
      navigate('/admin/inventoryTypes');
    } catch (err) {
      setOpenDelete(false);

      if (getErrorCode(err) === 422) {
        Snackbar.warning({
          message:
            'Work is associated with an inventory asset of this inventory type. Deletion is not allowed.',
        });
      } else {
        Snackbar.error({
          message: 'Failed to delete inventory type.',
        });
      }
    }
  };

  const tabs = getTabs(schemaTree?.isMaterial);

  const containerStyle = clsx(
    classes.headerContainer,
    isPreviewView && classes.preview,
  );

  const showEditButton = !schemaTree?.isPublished;
  const icon = getLocationTypeIcon(schemaTree?.locationType);
  const viewIcon = isPreviewView ? editSchemaIcon : tabIcon;
  const viewTooltip = isPreviewView
    ? 'Edit Attributes and Sub Items'
    : 'View Details';

  return (
    <>
      <div className={containerStyle}>
        <div className={classes.topHeader}>
          <div className={classes.headerLeft}>
            <IconButton onClick={handleBackClick} tooltip="Go back">
              <Icon icon={ArrowBackIcon} color={colors.neutral.white} />
            </IconButton>
            <div className={classes.titleContainer}>
              {icon}
              <div className={classes.titleText}>{schemaTree?.name}</div>
            </div>
            {hasPendingChanges && <div>{`(Unpublished changes)`}</div>}
          </div>
          <div className={classes.headerRight}>
            {loading && <Progress size={25} thickness={2} />}
            {showEditButton && (
              <IconButton
                onClick={() => setOpenEdit(true)}
                tooltip="Edit"
                data-cy="schemaDetailHeaderEditButton"
              >
                <Icon icon={EditIcon} color={colors.neutral.white} />
              </IconButton>
            )}
            <IconButton onClick={toggleView} tooltip={viewTooltip}>
              <img src={viewIcon} />
            </IconButton>
            <IconButton onClick={() => setOpenDelete(true)} tooltip="Delete">
              <Icon icon={DeleteIcon} color={colors.neutral.white} />
            </IconButton>
            <SchemaStatus />
          </div>
        </div>
        {isPreviewView && (
          <div>
            <div className={classes.infoBoxesWrapper}>
              <div className={clsx(classes.infoBox, classes.label)}>
                <div>Asset Name:</div>
                <div>Last Updated:</div>
              </div>
              <div className={clsx(classes.infoBox, classes.value)}>
                <div>-</div>
                <div>-</div>
              </div>
              <div className={clsx(classes.infoBox, classes.label)}>
                <div>Last Work ID:</div>
                <div>Last Work ID Updated:</div>
              </div>
              <div className={clsx(classes.infoBox, classes.value)}>
                <div>-</div>
                <div>-</div>
              </div>
            </div>
            <div className={classes.iconRow}>
              {tabs.map(tab => {
                return <HeaderNavigationButton key={tab.value} view={tab} />;
              })}
            </div>
          </div>
        )}
      </div>
      <CreateEditSchemaModal
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        onSave={handleUpdate}
        schema={schemaTree}
      />
      <DeleteModal
        open={openDelete}
        onConfirm={handleDelete}
        onCancel={() => setOpenDelete(false)}
        title={`Delete ${schemaTree?.name} Inventory Type?`}
        content="Deleting this inventory type will also delete all items under it and all associated assets."
      />
      <DeleteModal
        open={openDiscard}
        title="Discard Changes?"
        content="Changes will not be saved. Are you sure you want to discard all changes?"
        onCancel={() => setOpenDiscard(false)}
        onConfirm={navigateBack}
        confirmText="Discard"
      />
    </>
  );
};

export default SchemaDetailHeader;
